import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BriefcaseBusiness, CalendarDays, Clock3, Globe, GraduationCap, Languages, MapPin, ThumbsUp, UploadIcon } from "lucide-react";
import { LuLinkedin } from "react-icons/lu";
import { Button } from "@/components/ui/Button";
import { Card, CardHeader } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Error } from "@/components/shared/Error";
import { JobService } from "@/services/jobService";
import OneLineSkills from "@/components/shared/OneLineSkills";
import LoadingSkeleton from "./components/LoadingSkeleton";
import InfoCard from "./components/InfoCard";
import { parseArray } from "@/utils/parseArray";
import { toLanguageStrings } from "@/utils/languageRequirements";
import { formatExperienceRange } from "@/utils/experience";
import { formatDate, getDaysAgo } from "@/utils/dateUtils";
import useLiveResource from "@/hooks/useLiveResource";
import { getTenantSlug } from "@/utils/tenantSlug";
import { EMPLOYEE_TYPE } from "@/config/dropdownData";
import { cn } from "@/lib/utils";

const getEmployeeTypeLabel = (value) => {
  return EMPLOYEE_TYPE.find((item) => item.value === value)?.label || value;
};
const JobDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const [jobDetails, setJobDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portalUnavailable, setPortalUnavailable] = useState(false);

  const badgeItems = [
    {
      icon: <MapPin size={14} />,
      label: jobDetails?.location || "N/A",
      href: jobDetails?.location ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(jobDetails.location)}` : null,
    },
    {
      icon: <BriefcaseBusiness size={14} />,
      label: getEmployeeTypeLabel(jobDetails?.employee_type) || "Full-time",
    },
    {
      icon: <Clock3 size={14} />,
      label: jobDetails?.shift_timing || "N/A",
    },
    {
      icon: <CalendarDays size={14} />,
      label: jobDetails?.calendar_type_name || "N/A",
    },
  ];

  // ─── FETCH ────────────────────────────────────────────────────────────────
  const fetJobDetails = useCallback(
    async ({ silent = false } = {}) => {
      setError(null);
      if (!silent) {
        setJobDetails(null);
        setIsLoading(true);
      }
      try {
        const data = await JobService.fetJobDetails(jobId);
        setJobDetails(data.data ?? null);
        setPortalUnavailable(false);
      } catch (e) {
        // Tenant deactivated/deleted — backend blocks the whole public portal
        // with this error_code (see server/app/core/tenant_db.py get_public_db).
        if (e?.response?.data?.error_code === "PORTAL_UNAVAILABLE") {
          setPortalUnavailable(true);
        } else if (!silent) {
          setError(e.message ?? "Failed to load job detail.");
        }
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [jobId],
  );

  // ─── EFFECTS ──────────────────────────────────────────────────────────────
  useEffect(() => {
    fetJobDetails();
  }, [fetJobDetails]);

  // Visibility fallback — silently refresh when user returns to this tab
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") fetJobDetails({ silent: true });
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetJobDetails]);

  // ─── WEBSOCKET ────────────────────────────────────────────────────────────

  // `job:<id>` is this one job, so another job's event never reaches this page
  // and no `match` is needed. Every job event is a silent refetch: the three
  // handlers that used to patch `status` in place had a status to patch, and
  // the envelope no longer carries one.
  useLiveResource({
    keys: `job:${jobId}`,
    enabled: Boolean(jobId),
    transport: "public",
    slug: getTenantSlug(),
    onChange: () => fetJobDetails({ silent: true }),
  });

  // ─── DERIVED ──────────────────────────────────────────────────────────────
  const skills = jobDetails?.skills || [];

  const formatBulletList = (value) => {
    const text = Array.isArray(value) ? value.join("\n") : String(value || "");

    return text
      .split("\n")
      .map((item) => item.trim().replace(/^[•.-]\s*/, ""))
      .filter(Boolean);
  };

  const responsibilities = formatBulletList(jobDetails?.key_responsibilities);

  const niceToHave = formatBulletList(jobDetails?.nice_to_have);

  // Stored pairs render as "English — Fluent"; legacy string rows as the
  // bare language.
  const languagesRequired = toLanguageStrings(jobDetails?.languages_required).join(", ");

  const companyLinks = [
    jobDetails?.company_link && {
      label: "Visit Website",
      href: jobDetails.company_link,
      icon: Globe,
    },

    jobDetails?.company_linkedin_link && {
      label: "LinkedIn",
      href: jobDetails.company_linkedin_link,
      icon: LuLinkedin,
    },
  ].filter(Boolean);

  const experienceLabel = formatExperienceRange(jobDetails?.experience_min, jobDetails?.experience_max);

  const jobStats = [
    {
      label: "Total Openings",
      value: jobDetails?.vacancy_count ?? 0,
    },
    {
      label: "Applicants",
      value: jobDetails?.candidate_count ?? 0,
    },
    {
      label: "Apply Before",
      value: formatDate(jobDetails?.deadline),
    },
    {
      label: "Job Posted",
      value: formatDate(jobDetails?.created_at),
    },
    ...(experienceLabel ? [{ label: "Experience", value: experienceLabel }] : []),
  ];

  // ─── RENDER ───────────────────────────────────────────────────────────────
  if (portalUnavailable) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-20 font-sans text-center" style={{ zoom: 0.95 }}>
        <svg className="w-10 h-10 text-text-secondary mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
          />
        </svg>
        <h1 className="text-text-primary font-bold text-xl leading-tight mb-1">Career portal unavailable</h1>
        <p className="text-text-secondary text-sm">This career portal is currently unavailable. Please check back later.</p>
      </div>
    );
  }

  return (
    <div className="bg-background-primary !text-text-primary mt-2">
      <div className="px-4 sm:px-6 md:px-10 max-w-5xl mx-auto">
        {isLoading ? (
          <div className="mb-6 animate-pulse">
            <div className="h-10 w-40 rounded-md bg-zinc-700" />
          </div>
        ) : (
          <Button className="rounded-md mb-3 mt-1 border border-border-primary text-xs px-4 py-2 bg-background-secondary" onClick={() => navigate("/user/job-listing")}>
            <span className="flex items-center gap-2">
              <ArrowLeft size={12} /> Back to Jobs
            </span>
          </Button>
        )}

        <Card className="md:p-5 p-3 mb-3 rounded-xl bg-transparent md:bg-background-secondary md:border md:border-border-primary border-0 !text-text-primary">
          {isLoading ? (
            <LoadingSkeleton />
          ) : error ? (
            <Error error={error} onHandleRetry={fetJobDetails} />
          ) : (
            <>
              <CardHeader className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-6 p-0">
                <div>
                  <h1 className="md:text-2xl font-semibold mb-3">
                    {jobDetails?.title || "N/A"}
                  </h1>
                  {/* {(jobDetails?.division || jobDetails?.department) && (
                    <p className="text-sm !text-text-secondary mb-3">
                      {[jobDetails?.division && `Division: ${jobDetails.division}`, jobDetails?.department && `Department: ${jobDetails.department}`]
                        .filter(Boolean)
                        .join(" | ")}
                    </p>
                  )} */}
                  <div className="flex flex-wrap items-center md:text-[10px] text-[8px] text-text-primary py-1 my-3 bg-background-tertiary/40 rounded-lg">
                    {jobStats.map((e, i) => (
                      <span
                        className={cn(
                          "flex flex-col lg:flex-row items-center gap-1 py-1 px-1.5 md:px-2", // reduced md:px-4 → md:px-2
                          jobStats.length - 1 !== i ? "border-r border-border-primary" : "",
                        )}
                        key={i}
                      >
                        <span>{e.label}:</span> {e.value}
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-3 flex-wrap">
                    {badgeItems.map((item, i) => (
                      <Badge
                        key={i}
                        label={
                          item.href ? (
                            <a href={item.href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:underline">
                              {item.icon}
                              <span>{item.label}</span>
                            </a>
                          ) : (
                            <span className="flex items-center gap-1">
                              {item.icon}
                              <span>{item.label}</span>
                            </span>
                          )
                        }
                        className="!rounded-full px-1 sm:px-3 lg:px-3 !text-[10px] sm:!text-xs md:!text-[10px]"
                      />
                    ))}
                  </div>
                </div>
                <Link to={`/user/apply/${jobId}`} state={{ jobTitle: jobDetails?.title }}>
                  <Button className="hidden items-center gap-2 md:flex w-full md:w-auto bg-button-secondary hover:bg-blue-700 text-text-base py-2 px-8 md:px-10 rounded-full text-sm whitespace-nowrap ">
                    <UploadIcon size={16} /> <span> Upload Resume</span>
                  </Button>
                </Link>
              </CardHeader>

              <OneLineSkills skills={skills} className="mb-2" badgeStyle="text-xs p-1" />

              <div className="fixed bottom-0 left-0 right-0 p-3 md:hidden z-50">
                <Link to={`/user/apply/${jobId}`} state={{ jobTitle: jobDetails?.title }}>
                  <Button className="w-full flex items-center gap-2 justify-center bg-button-secondary hover:bg-blue-700 text-text-base py-3 rounded-full text-sm">
                    <UploadIcon size={16} /> <span>Upload Resume</span>
                  </Button>
                </Link>
              </div>

              <div className="mb-4 mt-3">
                <h2 className="text-lg font-semibold mb-3">Role Overview</h2>
                <p className="text-xs leading-relaxed">{jobDetails?.role_overview || "No overview available"}</p>
              </div>

              <div className="mb-4">
                <h2 className="text-lg font-semibold mb-3">Key Responsibilities</h2>
                <div className="text-xs space-y-1">
                  {responsibilities.map((item, i) => (
                    <div key={item || i} className="flex gap-2 items-start">
                      <span className="mt-[6px] w-1.5 h-1.5 bg-text-primary rounded-full" />
                      <p>{item}</p>
                    </div>
                  ))}
                </div>
              </div>

              {(niceToHave.length > 0 || parseArray(jobDetails?.certifications).length > 0 || languagesRequired.length > 0) &&
                (() => {
                  const cardCount = [
                    niceToHave.length > 0,
                    parseArray(jobDetails?.certifications).length > 0,
                    languagesRequired.length > 0,
                  ].filter(Boolean).length;

                  const gridCols = cardCount === 1 ? "grid-cols-1" : cardCount === 2 ? "grid-cols-1 xl:grid-cols-2" : "grid-cols-1 xl:grid-cols-3";

                  return (
                    <div className={`grid ${gridCols} gap-4 mb-8`}>
                      {niceToHave.length > 0 && (
                        <InfoCard
                          title="Nice to have"
                          icon={ThumbsUp}
                          iconBg="bg-[#FFDEEA]"
                          iconColor="text-[#C24471]"
                          text={niceToHave}
                          glowColor="rgba(255, 103, 159, 0.35)"
                        />
                      )}
                      {parseArray(jobDetails?.certifications).length > 0 && (
                        <InfoCard
                          title="Certifications"
                          icon={GraduationCap}
                          iconBg="bg-[#BFE0FF]"
                          iconColor="text-[#0083FF]"
                          text={parseArray(jobDetails?.certifications)}
                          glowColor="rgba(96, 165, 250, 0.35)"
                          gradient="bg-[linear-gradient(to_right,#0083FF4D_0%,transparent_60%),linear-gradient(to_bottom,#0083FF4D,rgba(0,131,255,0.25),transparent),linear-gradient(to_top,rgba(0,131,255,0.2),transparent)]"
                        />
                      )}
                      {languagesRequired.length > 0 && (
                        <InfoCard
                          title="Languages Required"
                          icon={Languages}
                          iconBg="bg-[#DFFFE9]"
                          iconColor="text-[#6DAF81]"
                          text={languagesRequired}
                          glowColor="rgba(74, 222, 128, 0.35)"
                          blur="20px"
                          gradient="bg-[linear-gradient(to_right,#6DAF814D_0%,transparent_60%),linear-gradient(to_bottom,#6DAF814D,rgba(109,175,129,0.25),transparent),linear-gradient(to_top,rgba(109,175,129,0.2),transparent)]"
                        />
                      )}
                    </div>
                  );
                })()}

              {companyLinks.length > 0 && (
                <>
                  <div className="border-t border-border-primary -mx-5" />

                  <div className="pt-6">
                    <p className="text-lg mb-3">Company</p>

                    <div className="flex gap-3 flex-wrap">
                      {companyLinks.map(({ label, href, icon: Icon }, index) => (
                        <a
                          key={index}
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 rounded-md mb-6 border font-semibold border-border-primary text-sm px-3 py-2 bg-background-tertiary hover:bg-background-tertiary/30 transition"
                        >
                          {Icon && <Icon size={16} />}
                          {label}
                        </a>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default JobDetails;
